import { Box, Container, Stack, Typography, useTheme } from '@mui/material'
import { useRef } from 'react'
import { useNavigate } from 'react-router'
import { ContainedButton, OutlinedButton } from '../../../components/Button'
import { TestStripFooter } from '../../../components/Footer'
import { Form } from '../../../components/Footer/subComponents/Form'
import { FlaskIcon, LightenIcon, ShieldIcon } from '../../../components/icons'
import { FeatureItem } from './subComponents'
import { WaterTestStripActions } from '../../../store/waterTestStrip'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '../../../store'
import { WaterTestFormActions } from '../../../store/waterTestForm'

export const Landing = () => {
  const theme = useTheme()
  const dispatch = useDispatch<AppDispatch>()
  const footerRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        minHeight: 'calc(100vh - 64px)',
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'center',
        pt: 6,
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          pb: 10,
        }}
      >
        <Typography
          fontSize={{ xs: '2.8rem', md: '3.3rem' }}
          lineHeight={'1.2'}
          fontWeight={theme.typography.fontWeightBold}
          color="text.primary"
          sx={{ mb: 2 }}
        >
          Instant Water Quality Analysis
          <br />
          with AI Precision
        </Typography>

        <Typography
          width={{ xs: '100%', md: '70%' }}
          textAlign={'center'}
          margin={'auto'}
          fontSize={'1.1rem'}
          fontWeight={theme.typography.fontWeightMedium}
          color={theme.palette.grey[600]}
          sx={{ mb: 4 }}
        >
          Get accurate results in seconds. Simply dip the test strip and capture your water quality
          with AI.
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} gap={2} mb={{xs: 3, md: 6}} width={{ xs: '100%', md: '40%' }}>
            <ContainedButton
            size="large"
            fullWidth
            onClick={() => {
              navigate('/test-strip/steps')
            }}
            sx={{
              py: 1.5,
              order: { xs: 2, md: 1 } ,
              px: 4,
            }}
          >
            Analyze your water
          </ContainedButton>
          <OutlinedButton
            fullWidth
            size="large"
            onClick={() => {
              dispatch(WaterTestFormActions.resetForm())
              dispatch(WaterTestStripActions.resetWaterTestStripData())
              footerRef.current?.scrollIntoView({ behavior: 'smooth' })
            }}
            sx={{
              py: 1.5,
              px: 4,
              backgroundColor: 'white',
              border: '1px solid #D0D5DD',
              color: 'text.primary',
              order: { xs: 1, md: 2 },
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderColor: '#C0C5CC'
              }
            }}
          >
            Request a test strip
          </OutlinedButton>
          </Stack>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 3,
            mt: 4,
          }}
          flexDirection={{ xs: 'column', md: 'row' }}
        >
          <FeatureItem
            icon={<ShieldIcon sx={{ fontSize: '4rem' }} />}
            title="Safety insights"
            description="Learn about each attribute and how SIPP protects you from them."
          />
          <FeatureItem
            icon={<FlaskIcon sx={{ fontSize: '4rem' }} />}
            title="Comprehensive testing"
            description="We build on the utility data by testing your home's water for 16 attributes that play a pivotal role in water quality."
          />
          <FeatureItem
            icon={<LightenIcon sx={{ fontSize: '4rem' }} />}
            title="Instant results"
            description="AI-powered rapid analysis with informative insights"
          />
        </Box>
      </Container>
      <TestStripFooter>
        <Box ref={footerRef} sx={{ pt: 5 }} alignSelf={{ xs: 'stretch', md: 'auto' }}>
          <Form />
        </Box>
      </TestStripFooter>
    </Box>
  )
}